import { InputBaseReactComponent } from './../input-base-react/input-base-react.component';
import { AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { InputPasswordReactMode } from './input-password-react.enum';


@Component({
  selector: 'app-input-password-react',
  templateUrl: './input-password-react.component.html',
  styleUrls: ['./input-password-react.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputPasswordReactComponent),
      multi: true
    }
  ]
})

export class InputPasswordReactComponent extends InputBaseReactComponent implements ControlValueAccessor, OnInit, OnChanges, AfterViewInit {

  //#region .  Variaveis  .

  inputModeEnum = InputPasswordReactMode;
  spanIdenticalFields?: string = undefined;
  spanMaxSize?: string = undefined;
  spanChangePassword?: string = undefined;
  confirmControl: FormControl = undefined;
  changePasswordControl: FormControl = undefined;
  passwordFieldName = '';

  //#endregion

  //#region .  Inputs  .

  @ViewChild('confirmField') confirmField: ElementRef;

  @Input() confirmValue = '';
  @Input() confirmPasswordLabel?: string = undefined;
  @Input() confirmPasswordFieldName = '';
  @Input() inputMode: InputPasswordReactMode = InputPasswordReactMode.iprmInsert;
  @Input() changePassword = false;

  @Output() confirmValueChange: EventEmitter<string> = new EventEmitter<string>();

  //#endregion

  //#region .  LifeCycle Methods e Construtor
  constructor() {
    super();
    this.passwordFieldName = this.name;
  }

  // efetua instância objeto FormControl a partir de nome do input passado para o component
  // caso o component esteja utilizando o inputMode, é inserido o validador validateConfirmação, que é disparado ao
  // digitar um valor no campo senha.
  // é passado para o validador via bind o valor "this", para que dentro do evento do validador o objeto do tipo
  // campoPasswordReactComponent esteja visivel.
  ngOnInit(): void {
    super.ngOnInit();

    if (this.form) {
      const comp = this.form.get(this.confirmPasswordFieldName);
      if (comp instanceof FormControl) {
        this.confirmControl = comp as FormControl;
      }
    }

    this.loadValidators();
    this.setAndUpdateValidators();
  }

  // caso o controle esteja instanciado, e o component esteja sendo utilizado no inputMode,
  // os validadores são re-atribuidos.
  // no caso do component password, a obrigatoriedade é condicional, caso esteja como obrigatório,
  // é inserido o validador de obrigatoriedade, caso contrário, os valores dos campos senha e confirmar senha são zerados.
  // caso o component esteja utilizando o inputMode, é inserido o validador validateConfirmação, que é disparado ao
  // digitar um valor no campo senha.
  // é passado para o validador via bind o valor "this", para que dentro do evento do validador o objeto do tipo
  // campoPasswordReactComponent esteja visivel.
  ngOnChanges(): void {
    super.ngOnChanges();
    this.loadValidators();
    this.setAndUpdateValidators();
  }

  ngAfterViewInit(): void {
    if (this.inputMode === InputPasswordReactMode.iprmEdit) {
      this.changePassword = false;
    } else
      if (this.inputMode === InputPasswordReactMode.iprmInsert) {
        this.changePassword = true;
      }

    this.spanChangePassword = (this.changePassword ? 'Cancelar modificação de senha' : 'Mudar senha');

  }
  //#endregion

  //#region .  Getters and Setters  .

  // getter utilizado para retornar o valor da propriedade "confirmValue"
  get valueConfirm(): string {
    return this.confirmValue;
  }

  // setter utilizado para modificar o valor da propriedade "confirmValue"
  set valueConfirm(val) {
    this.confirmValue = val;
    this.confirmValueChange.emit(this.confirmValue);
    this.onChange(this.confirmValue);
    this.onTouched();
  }
  //#endregion

  //#region .  Métodos  .

  // retorna o label que será utilizado no campo confirmar senha.
  // caso a propriedade confirmPasswordLabel tenha sido passada, a mesma será retornada, caso contrário
  // é e retornado o valor da propriedade "name".
  public confirmPasswordLabelName(): string {
    if (this.confirmPasswordLabel) {
      return this.confirmPasswordLabel;
    } else {
      return this.name;
    }
  }

  // evento para controlar modificações no campo confirmar senha.
  // é disparado internamente o evento utilizado no campo senha, para que o validador de confirmação
  // seja disparado.
  public changeValueConfirmField(event): void {
    this.changeValue(event);
    const newValue = this.confirmField.nativeElement.value;
    this.confirmValue = newValue;
    if (this.form.get(this.confirmPasswordFieldName)) {
      this.form.get(this.confirmPasswordFieldName).setValue(newValue);
    }
  }

  // evento para controlar se o componente está em modo edição ou não
  public setChangePassword(event): void {
    event.preventDefault();

    this.changePassword = !this.changePassword;

    if (!this.changePassword) {
      this.value = '';
      this.valueConfirm = '';
      this.field.nativeElement.value = '';
      this.confirmField.nativeElement.value = '';
    }

    this.spanChangePassword = (this.changePassword ? 'Cancelar modificação de senha' : 'Mudar senha');
  }

  public disableFields(): boolean {
    if (this.inputMode !== InputPasswordReactMode.iprmEnterPassword) {
      return !this.changePassword || this.readOnly;
    } else {
      return this.readOnly;
    }
  }

  // validador utilizado para verificar se os campos senha e confirmar senha são iguais.
  // caso os valores sejam distintos o componente fica com estado de invalid.
  protected validateConfimacao(c: FormControl): any {
    let output: boolean;

    if (this.changePassword) {
      // tslint:disable-next-line:prefer-conditional-expression
      if (!this.confirmField || !this.field) {
        output = false;
      } else {
        output = this.field.nativeElement.value === this.confirmField.nativeElement.value;
      }
    } else {
      output = true;
    }

    return output ? undefined :
      {
        confirmValidate: {
          valid: false
        }
      };
  }

  // retorna se o campo é obrigatório e preenche span de obrigatoriedade de acordo com o modo de cadastro que o component
  // esta sendo utilizado.
  validateRequired(): boolean {
    let output = false;

    // tslint:disable-next-line:prefer-conditional-expression
    if (this.changePassword || this.inputMode === InputPasswordReactMode.iprmEnterPassword) {
      output = !super.validateRequired();
    } else {
      output = false;
    }

    // tslint:disable-next-line:prefer-conditional-expression
    if (this.inputMode === InputPasswordReactMode.iprmInsert && this.changePassword) {
      // tslint:disable-next-line:prefer-template
      this.spanRequired = (output ? '' : this.getLabelName() + ' e ' + this.confirmPasswordLabelName() + ' são obrigatórios');
    } else {
      // tslint:disable-next-line:prefer-template
      this.spanRequired = (output ? '' : this.getLabelName() + ' é obrigatório');
    }

    return output;
  }

  // caso o component esteja sendo utilizado em modo cadastro, é efetuada a comparação dos valores dos dois inputs,
  // caso os valores sejam distintos, o span de mensagem de obrigatoriedade é preenchido e o retorno e falso.
  // caso sejam identicos, ou o component não esteja no modo cadastro, é retornado como valido.
  validateIdenticalFields(): boolean {
    let output = false;

    if (this.inputMode !== InputPasswordReactMode.iprmEnterPassword) {
      output = (this.value === this.confirmValue);
      this.spanIdenticalFields = (output ? '' : 'Os valores digitados não estão iguais');
    } else {
      output = true;
      this.spanIdenticalFields = '';
    }

    return output;
  }
  //#endregion

  // remove todos os validadores vinculados ao component e refaz a atribuição
  private loadValidators(): void {
    if (this.inputMode !== InputPasswordReactMode.iprmEnterPassword) {
      this.insertValidator(this.validateConfimacao.bind(this));
    }
  }
}
